import { format } from "date-fns";
import { CalendarDays, ChevronDown, Filter, Search } from "lucide-react";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaSyncAlt } from "react-icons/fa";
import "../../../datepicker.css";
const ApplicationsTable = ({ applications = [], loading }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("All");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Date filter states
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [pickerMode, setPickerMode] = useState("date"); // "date" | "month" | "year"
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Selection states
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Convert incoming props into table-friendly structure
  const allApplications = applications.map((app) => {
    const calculateDaysInProcess = (createdAt) => {
      if (!createdAt) return 0;
      const created = new Date(createdAt);
      const now = new Date();
      const diffTime = Math.abs(now - created);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    return {
      id: app._id || app.distributionId || app.loan?.id || "N/A",
      name:
        app.loan?.user?.name ||
        app.loan?.username ||
        app.user?.fullName ||
        "N/A",
      amount: app.loan?.principle || app.approveAmount || app.principle || 0,
      type: app.loan?.loanType || app.loanType || "Personal Loan",
      date: app.createdAt || app.loan?.createdAt || new Date().toISOString(),
      status: app.status || "processing",
      days: app.daysInProcess || calculateDaysInProcess(app.createdAt) || 0,
    };
  });

  // Filtering logic
  const filteredApps = allApplications.filter((app) => {
    const matchesSearch =
      app?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app?.id?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      app?.loanType?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = status === "All" ? true : app.status === status;

    let matchesDate = true;
    if (pickerMode === "date") {
      matchesDate =
        (!startDate || new Date(app.date) >= new Date(startDate)) &&
        (!endDate || new Date(app.date) <= new Date(endDate));
    } else if (pickerMode === "month" && startDate) {
      const appDate = new Date(app.date);
      matchesDate =
        appDate.getMonth() === startDate.getMonth() &&
        appDate.getFullYear() === startDate.getFullYear();
    } else if (pickerMode === "year" && startDate) {
      const appDate = new Date(app.date);
      matchesDate = appDate.getFullYear() === startDate.getFullYear();
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Select all toggle
  const handleSelectAll = () => {
    setSelectedAll(!selectedAll);
    if (!selectedAll) {
      setSelectedRows(allApplications.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  // Checkbox toggle
  const handleCheckboxChange = (index) => {
    const updatedSelection = selectedRows.includes(index)
      ? selectedRows.filter((i) => i !== index)
      : [...selectedRows, index];
    setSelectedRows(updatedSelection);
    setSelectedAll(updatedSelection.length === allApplications.length);
  };

  const getPickerLabel = () => {
    if (pickerMode === "month") return "Choose Month";
    if (pickerMode === "year") return "Choose Year";
    return "Choose Dates";
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        <div className="w-8 h-8 border-4 border-[#FFCA20] border-t-transparent rounded-full animate-spin"></div>
        <div className="text-customWhite text-sm">Loading applications...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="my-2">
        <h2 className="font-semibold">Current Applications</h2>
      </div>

      <div className="p-2 md:p-4 space-y-3 md:space-y-0 md:flex md:justify-between">
        {/* Search */}
        <div className="relative w-full md:w-64 text-white">
          <div className="absolute left-3 top-2.5 text-gray-400">
            <Search size={16} />
          </div>
          <input
            type="text"
            placeholder="Search Applications"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-black rounded-lg px-10 py-2 text-sm w-full border border-gray-700"
          />
        </div>

        {/* Filters */}
        <div className="flex space-x-2 relative">
          {/* Date Picker with modes */}
          <div className="relative flex-1 sm:w-auto">
            <button
              className="bg-transparent border-2 border-[#aaaaaa] hover:border-[#FFCA20] h-10 text-gray-300 px-2 md:px-4 py-2 rounded-lg flex items-center justify-center w-full text-sm m-1"
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <CalendarDays size={16} className="mr-1" color="#FFCA20" />
              <span className="text-[#ffca20]">
                {startDate && (pickerMode === "date" ? endDate : startDate)
                  ? pickerMode === "date"
                    ? `${startDate.toLocaleDateString()} - ${endDate?.toLocaleDateString()}`
                    : pickerMode === "month"
                    ? format(startDate, "MMM yyyy")
                    : format(startDate, "yyyy")
                  : getPickerLabel()}
              </span>
            </button>

            {isDatePickerOpen && (
              <div className="absolute z-50 mt-2 bg-[#1e1e1e] rounded-md p-3 shadow-lg">
                {/* Mode Switch */}
                <div className="flex gap-2 mb-2">
                  {["date", "month", "year"].map((mode) => (
                    <button
                      key={mode}
                      className={`px-3 py-1 rounded ${
                        pickerMode === mode
                          ? "bg-yellow-500 text-black"
                          : "bg-[#333] text-white"
                      }`}
                      onClick={() => setPickerMode(mode)}
                    >
                      {mode === "date"
                        ? "Date"
                        : mode === "month"
                        ? "Month"
                        : "Year"}
                    </button>
                  ))}
                </div>

                <DatePicker
                  selected={startDate}
                  onChange={(update) => {
                    if (pickerMode === "date") {
                      setDateRange(update);
                      if (update[0] && update[1]) setIsDatePickerOpen(false);
                    } else {
                      setDateRange([update, update]);
                      setIsDatePickerOpen(false);
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange={pickerMode === "date"}
                  showMonthYearPicker={pickerMode === "month"}
                  showYearPicker={pickerMode === "year"}
                  inline
                />
              </div>
            )}
          </div>

          {/* Status Custom Select */}
          <div className="relative flex-1 sm:w-auto">
            <button
              className="bg-transparent border-2 border-[#aaaaaa] hover:border-[#FFCA20] h-10 text-gray-300 px-2 md:px-4 py-2 rounded-lg flex items-center justify-center w-full text-sm m-1"
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            >
              <Filter size={16} className="mr-1" color="#FFCA20" />
              <span className="text-[#ffca20] truncate">
                <span className="hidden sm:inline">Status: </span>
                {status}
              </span>
              <ChevronDown
                size={16}
                className="ml-1 md:ml-2 text-[#FFCA20] flex-shrink-0"
              />
            </button>
            {showStatusDropdown && (
              <div className="border border-gray-700 absolute top-12 left-0 sm:right-0 sm:left-auto bg-black text-[#ffca20] p-2 rounded-lg shadow z-50 min-w-40">
                {["All", "approved", "processing", "in-review", "rejected"].map(
                  (s) => (
                    <div
                      key={s}
                      className="cursor-pointer hover:bg-gray-800 px-2 py-1 rounded text-sm"
                      onClick={() => {
                        setStatus(s);
                        setShowStatusDropdown(false);
                      }}
                    >
                      {s === "approved"
                        ? "Approved"
                        : s === "processing"
                        ? "Processing"
                        : s === "in-review"
                        ? "In Review"
                        : s === "rejected"
                        ? "Rejected"
                        : s}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs py-1 text-[#F3F3F3]">
        Select to enable bulk options{" "}
        <span className="ml-2 text-sm text-blue-400">
          ({selectedRows.length} selected)
        </span>
      </p>

      {/* Table */}
      <div className="bg-[#131313] rounded-lg border border-gray-800 w-full overflow-x-auto scrollbar-hide">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full bg-[#131313]">
            <thead className="bg-[#131313] text-xs text-gray-500 uppercase border-y border-gray-800">
              <tr>
                <th className="w-4 h-4 bg-[#131313] rounded-sm">
                  <input
                    type="checkbox"
                    className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:border-yellow-400 relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                    checked={selectedAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="px-4 py-3 text-left border border-gray-800">
                  APPLICATION ID
                </th>
                <th className="px-4 py-3 text-left border border-gray-800">
                  APPLICANT NAME
                </th>
                <th className="px-4 py-3 text-left border border-gray-800">
                  LOAN AMOUNT
                </th>
                <th className="px-4 py-3 text-left border border-gray-800">
                  EXPIRING DATE
                </th>
                <th className="px-4 py-3 text-left border border-gray-800">
                  STATUS
                </th>
                <th className="px-4 py-3 text-left border border-gray-800">
                  DAYS IN PROCESS
                </th>
                <th className="px-4 py-3 text-left border border-gray-800">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filteredApps.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                        <svg
                          className="w-8 h-8 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-white mb-2">
                        No Applications Found
                      </h3>
                      <p className="text-gray-400 text-sm">
                        {applications.length === 0
                          ? "No applications have been assigned to this banking partner yet."
                          : "No applications match your current filters. Try adjusting your search criteria."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredApps.map((row, index) => (
                  <tr key={index} className="border-customBorder">
                    <td className="px-4 border border-gray-800 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(index)}
                        onChange={() => handleCheckboxChange(index)}
                        className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:border-yellow-400 relative cursor-pointer
                          before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      />
                    </td>
                    <td className="px-4 border border-gray-800 py-3">
                      <span className="font-mono text-sm">{row.id}</span>
                    </td>
                    <td className="px-4 border border-gray-800 py-3">
                      <div className="font-medium">{row.name}</div>
                    </td>
                    <td className="px-4 border border-gray-800 py-3">
                      <div className="font-semibold">
                        ₹{row.amount.toLocaleString("en-IN")}
                      </div>
                      <div className="text-xs text-gray-400">{row.type}</div>
                    </td>
                    <td className="px-4 border border-gray-800 py-3">
                      <span className="text-sm">
                        {row.date && row.date !== "N/A"
                          ? format(new Date(row.date), "MMM dd, yyyy")
                          : "N/A"}
                      </span>
                    </td>
                    <td className="px-4 border border-gray-800 py-3">
                      <span
                        className={`px-3 py-1 rounded-xl text-xs ${
                          row.status === "approved"
                            ? "bg-[#D7F8E3] text-[#00922F]"
                            : row.status === "processing" ||
                              row.status === "in-review"
                            ? "bg-[#423715] text-[#FFCA20]"
                            : "bg-[#FFE0E0] text-[#FF0000]"
                        }`}
                      >
                        {row.status === "approved"
                          ? "Approved"
                          : row.status === "processing"
                          ? "Processing"
                          : row.status === "in-review"
                          ? "In Review"
                          : row.status === "rejected"
                          ? "Rejected"
                          : row.status}
                      </span>
                    </td>
                    <td className="px-4 border border-gray-800 text-center py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-800 text-gray-300">
                        {row.days} {row.days === 1 ? "day" : "days"}
                      </span>
                    </td>
                    <td className="px-4 border border-gray-800 py-3">
                      <button
                        className={`flex items-center bg-[#131313] ${
                          row.status === "approved" || row.status === "rejected"
                            ? "text-[#333333] cursor-not-allowed"
                            : "text-customYellow"
                        }`}
                        disabled={
                          row.status === "approved" || row.status === "rejected"
                        }
                      >
                        <FaSyncAlt size={16} className="mr-1" />
                        <span>Re-allocate</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsTable;
